import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, Heart, Minus, Plus, Star, X } from "lucide-react";
import { toast } from "sonner";
import { API, inr, usePageTitle, useShop } from "@/context/ShopContext";
import { FABRIC_INFO } from "@/lib/fabricInfo";
import ProductCard from "@/components/ProductCard";
import { PreorderPanel } from "@/components/PreorderPanel";
import Reveal from "@/components/Reveal";

const Stars = ({ value, size = "w-4 h-4" }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-rose text-rose" : "text-beige"}`} strokeWidth={1.5} />
    ))}
  </span>
);

function Accordion({ title, testId, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-beige">
      <button
        data-testid={testId}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-4 text-xs uppercase tracking-[0.2em] text-espresso"
      >
        {title}
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      <div className={`overflow-hidden transition-all duration-500 ${open ? "max-h-[30rem] opacity-100 pb-5" : "max-h-0 opacity-0"}`}>
        <div className="text-sm text-espresso/75 leading-relaxed space-y-2">{children}</div>
      </div>
    </div>
  );
}

function ShadePicker({ families, selected, onToggle, max }) {
  const [fam, setFam] = useState(families[0]?.name);
  const active = families.find((f) => f.name === fam) || families[0];
  if (!active) return null;
  return (
    <div data-testid="shade-picker">
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {families.map((f) => (
          <button
            key={f.name}
            data-testid={`picker-family-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFam(f.name)}
            className={`shrink-0 text-[11px] uppercase tracking-[0.15em] px-3.5 py-2 border transition-colors duration-300 ${
              fam === f.name ? "border-espresso text-espresso" : "border-beige text-taupe hover:border-taupe"
            }`}
          >
            {f.name}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-x-2 gap-y-4 mt-5">
        {active.shades.map((s) => {
          const label = s.collection ? `${s.name} · ${s.collection}` : s.name;
          const isSel = selected.includes(label);
          return (
            <button
              key={label}
              data-testid={`picker-shade-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => onToggle(label, max)}
              className="flex flex-col items-center gap-1.5 group"
              aria-pressed={isSel}
            >
              <span
                className={`w-9 h-9 rounded-full border border-white ring-1 ring-offset-2 ring-offset-ivory transition-all duration-300 ${
                  isSel ? "ring-espresso scale-110" : "ring-transparent group-hover:ring-taupe"
                }`}
                style={{ backgroundColor: s.hex }}
              />
              <span className={`text-[10px] tracking-wide text-center leading-tight ${isSel ? "text-espresso" : "text-taupe"}`}>
                {s.name}
                {s.collection && <span className="block text-[9px] text-taupe/70">{s.collection}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, meta, addToCart, toggleWishlist, isWishlisted, settings } = useShop();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0);
  const [selected, setSelected] = useState([]);
  const [reviewData, setReviewData] = useState({ reviews: [], count: 0, average: 0 });
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 0, text: "" });
  const [reviewBusy, setReviewBusy] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyState, setNotifyState] = useState("idle");
  usePageTitle(product ? `${product.name} — ${product.shade}` : "Product");

  useEffect(() => {
    setProduct(null); setNotFound(false); setQty(1); setImg(0); setSelected([]);
    setReviewData({ reviews: [], count: 0, average: 0 });
    setReviewForm({ name: "", rating: 0, text: "" });
    setNotifyEmail(""); setNotifyState("idle");
    fetch(`${API}/products/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setProduct)
      .catch(() => setNotFound(true));
    fetch(`${API}/products/${id}/reviews`)
      .then((r) => r.json())
      .then(setReviewData)
      .catch(() => {});
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.rating) {
      toast.error("Please choose a star rating.");
      return;
    }
    setReviewBusy(true);
    try {
      const r = await fetch(`${API}/products/${id}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reviewForm),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success("Thank you — your review is live.");
      setReviewForm({ name: "", rating: 0, text: "" });
      fetch(`${API}/products/${id}/reviews`).then((res) => res.json()).then(setReviewData);
    } catch (err) {
      toast.error(typeof err?.detail === "string" ? err.detail : "Could not submit review.");
    } finally {
      setReviewBusy(false);
    }
  };

  const submitNotify = async (e) => {
    e.preventDefault();
    try {
      const r = await fetch(`${API}/products/${id}/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: notifyEmail }),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      setNotifyState("done");
    } catch (err) {
      toast.error(typeof err?.detail === "string" ? err.detail : "Could not save your email.");
    }
  };

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-32 text-center" data-testid="product-not-found">
        <p className="font-serif text-3xl">This piece has slipped away</p>
        <Link to="/shop" className="btn-primary mt-8">Back to Shop</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid md:grid-cols-2 gap-12 animate-pulse">
        <div className="bg-beige aspect-[4/5]" />
        <div className="space-y-4 pt-8">
          <div className="h-4 bg-beige w-1/4" />
          <div className="h-8 bg-beige w-2/3" />
          <div className="h-4 bg-beige w-1/3" />
        </div>
      </div>
    );
  }

  const soldOut = product.stock === 0;
  const isFridayIST = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })).getDay() === 5;
  const daysToFriday = (5 - new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })).getDay() + 7) % 7;
  const wished = isWishlisted(product.id);
  const isBundle = product.type === "bundle";
  const needsSelection = isBundle && product.bundle_size > 0;
  const ready = !needsSelection || selected.length === product.bundle_size;
  const singles = products.filter((p) => p.type === "single");
  const familyShades = singles.filter((p) => p.colour_family === product.colour_family && p.id !== product.id);
  const sameCollection = product.collection_slug ? singles.filter((p) => p.collection_slug === product.collection_slug && p.id !== product.id) : [];
  const sameFabric = product.fabric ? singles.filter((p) => p.fabric === product.fabric && p.id !== product.id) : [];
  const related = (sameCollection.length ? sameCollection : sameFabric.length ? sameFabric : familyShades.length ? familyShades : singles.filter((p) => p.featured)).slice(0, 4);
  const relatedTitle = sameCollection.length ? `More from ${product.collection}` : sameFabric.length ? `More in ${product.fabric}` : "You May Also Love";
  const fabricInfo = FABRIC_INFO.find((f) => f.name === product.fabric);

  const toggleShade = (name, max) => {
    setSelected((s) => {
      if (s.includes(name)) return s.filter((x) => x !== name);
      if (s.length >= max) return [...s.slice(1), name];
      return [...s, name];
    });
  };

  const add = (silent) =>
    addToCart(product.id, qty, silent, needsSelection ? [...selected].sort() : null);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 md:py-16" data-testid="product-detail-page">
      <nav className="text-[11px] uppercase tracking-[0.2em] text-taupe mb-8" aria-label="Breadcrumb">
        <Link to="/shop" className="hover:text-espresso transition-colors">Shop</Link>
        <span className="mx-2">/</span>
        <span className="text-espresso">{isBundle ? "Sets" : product.shade}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
        <div>
          <div className="bg-beige overflow-hidden aspect-[4/5]">
            <img
              src={product.images[img]}
              alt={`${product.name} — ${product.shade}`}
              className="w-full h-full object-cover animate-fade-in"
              key={img}
            />
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-3 mt-3">
              {product.images.map((src, i) => (
                <button
                  key={src}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setImg(i)}
                  aria-label={`View image ${i + 1}`}
                  className={`w-20 aspect-[4/5] overflow-hidden bg-beige ring-1 ring-offset-2 ring-offset-ivory transition-all ${
                    img === i ? "ring-espresso" : "ring-transparent hover:ring-taupe"
                  }`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:pt-4">
          <p className="overline-label">{isBundle ? "Curated Set" : product.colour_family}</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight mt-3">{product.name}</h1>
          <p className="text-sm text-taupe mt-2">
            Shade: <span className="text-espresso">{product.shade}</span>
            {product.collection && product.type !== "bundle" && (
              <>
                <span className="mx-2 text-beige">·</span>
                Collection:{" "}
                <Link to={`/collections/${product.collection_slug}`} className="text-espresso underline decoration-beige underline-offset-4 hover:text-rose transition-colors" data-testid="pdp-collection">
                  {product.collection}
                </Link>
              </>
            )}
            {product.fabric && (
              <>
                <span className="mx-2 text-beige">·</span>
                Fabric: <span className="text-espresso" data-testid="pdp-fabric">{product.fabric}</span>
              </>
            )}
            {product.arabic_name && <span className="font-arabic text-base ml-2" lang="ar" dir="rtl">{product.arabic_name}</span>}
          </p>
          {product.colour_desc && (
            <p className="text-xs uppercase tracking-[0.15em] text-taupe mt-2" data-testid="pdp-colour-desc">{product.colour_desc}</p>
          )}
          <p className="text-xl mt-5" data-testid="product-price">{inr(product.price)}</p>
          <p className="text-sm text-espresso/75 leading-relaxed mt-6">{product.description}</p>

          {!isBundle && (
            <div className="mt-8">
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <Link
                  to={`/shop?family=${encodeURIComponent(product.colour_family)}`}
                  data-testid="view-family-shades"
                  className="inline-block text-[11px] uppercase tracking-[0.2em] text-taupe hover:text-espresso transition-colors border-b border-beige pb-0.5"
                >
                  All {product.colour_family} shades
                </Link>
                {product.fabric && (
                  <Link
                    to={`/shop?fabric=${encodeURIComponent(product.fabric)}`}
                    data-testid="view-fabric-hijabs"
                    className="inline-block text-[11px] uppercase tracking-[0.2em] text-taupe hover:text-espresso transition-colors border-b border-beige pb-0.5"
                  >
                    All {product.fabric} hijabs
                  </Link>
                )}
              </div>
            </div>
          )}

          {needsSelection && (
            <div className="mt-8">
              <p className="overline-label mb-4">
                Choose {product.bundle_size} Shades — <span className="text-espresso" data-testid="selection-count">{selected.length} of {product.bundle_size} selected</span>
              </p>
              <ShadePicker families={meta.families || []} selected={selected} onToggle={toggleShade} max={product.bundle_size} />
              {selected.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-5">
                  {selected.map((s) => (
                    <span key={s} className="inline-flex items-center gap-2 bg-white border border-beige px-3 py-1.5 text-xs text-espresso">
                      {s}
                      <button onClick={() => toggleShade(s, product.bundle_size)} aria-label={`Remove ${s}`} data-testid={`remove-shade-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                        <X className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-8">
            <p className="overline-label mb-4">Quantity</p>
            <div className="inline-flex items-center border border-beige">
              <button data-testid="qty-minus" aria-label="Decrease quantity" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3.5 text-espresso hover:bg-beige/50 transition-colors">
                <Minus className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <span data-testid="qty-value" className="w-12 text-center text-sm">{qty}</span>
              <button data-testid="qty-plus" aria-label="Increase quantity" onClick={() => setQty(Math.min(20, qty + 1))} className="p-3.5 text-espresso hover:bg-beige/50 transition-colors">
                <Plus className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-9">
            {product.preorder ? (
              <div className="flex-1"><PreorderPanel product={product} qty={qty} onApprovedAdd={() => add(false)} /></div>
            ) : product.friday_only && !isFridayIST ? (
              <button data-testid="friday-only-button" disabled className="btn-primary flex-1 opacity-60 cursor-not-allowed">
                {daysToFriday === 1 ? "Opens Tomorrow — Friday" : `Opens Friday — in ${daysToFriday} days`}
              </button>
            ) : (
            <button
              data-testid="add-to-bag-button"
              disabled={soldOut || !ready}
              onClick={() => add(false)}
              className="btn-primary flex-1"
            >
              {soldOut ? "Sold Out" : needsSelection && !ready ? `Select ${product.bundle_size} Shades` : "Add to Bag"}
            </button>
            )}
            <button
              data-testid="pdp-wishlist-button"
              aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
              onClick={() => toggleWishlist(product.id)}
              className="w-12 h-12 border border-beige flex items-center justify-center hover:border-espresso transition-colors"
            >
              <Heart className={`w-5 h-5 ${wished ? "fill-rose text-rose" : "text-espresso"}`} strokeWidth={1.5} />
            </button>
          </div>
          {!soldOut && !(product.friday_only && !isFridayIST) && (
            <button
              data-testid="buy-now-button"
              disabled={!ready}
              onClick={() => { add(true); navigate("/checkout"); }}
              className="btn-secondary w-full mt-3"
            >
              Buy Now
            </button>
          )}
          <p className="text-xs text-taupe mt-4" data-testid="stock-status">
            {product.friday_only && !isFridayIST ? `The ${product.collection} collection opens every Friday — ${daysToFriday === 1 ? "tomorrow" : `${daysToFriday} days to go`}. A complimentary LAMSAH mini attar is gifted with every Friday order. ♥` : soldOut ? "Currently sold out — back soon." : product.stock <= 8 ? `Only ${product.stock} left in stock.` : "In stock, ready to ship."}
          </p>

          {soldOut && (
            notifyState === "done" ? (
              <p className="text-xs text-rose mt-3" data-testid="notify-done">
                You're on the list — we'll email you the moment it's back.
              </p>
            ) : (
              <form onSubmit={submitNotify} className="flex gap-2 mt-4" data-testid="notify-form">
                <input
                  type="email"
                  required
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder="Your email — we'll tell you when it's back"
                  className="input-lux flex-1"
                  data-testid="notify-email-input"
                />
                <button type="submit" className="btn-secondary !px-5 shrink-0" data-testid="notify-submit">
                  Notify Me
                </button>
              </form>
            )
          )}

          <div className="mt-10 border-t border-beige">
            <Accordion title="Details" testId="accordion-details" defaultOpen>
              {!isBundle && <p>Colour family: {product.colour_family}</p>}
              <p>Fabric: {product.fabric || "Fabric not assigned"}</p>
              <p>Size: {product.length || "to be confirmed by LAMSAH"}</p>
            </Accordion>
            {product.id === "complete-collection" && (
              <Accordion title="What's Inside" testId="accordion-inside">
                {(meta.families || []).map((f) => (
                  <p key={f.name}>{f.name} — {f.shades.length} shades</p>
                ))}
              </Accordion>
            )}
            {fabricInfo && (
              <Accordion title={`About ${fabricInfo.name}`} testId="accordion-fabric">
                <p>{fabricInfo.intro}</p>
                {fabricInfo.specs.map(([k, v]) => (
                  <p key={k}>{k}: {v}</p>
                ))}
                <Link
                  to="/fabric-guide"
                  data-testid="pdp-fabric-guide-link"
                  className="inline-block text-[11px] uppercase tracking-[0.2em] text-rose border-b border-rose/40 pb-0.5 mt-1"
                >
                  Read the full Fabric Guide
                </Link>
              </Accordion>
            )}
            <Accordion title="Care Instructions" testId="accordion-care">
              <p>{product.care || fabricInfo?.care || "Hand wash cold with a gentle detergent. Do not bleach. Lay flat or hang to dry away from direct sunlight. Cool iron on reverse if needed."}</p>
            </Accordion>
            <Accordion title="Shipping & Returns" testId="accordion-shipping">
              <p>{settings.delivery_info}</p>
            </Accordion>
          </div>
        </div>
      </div>

      <section className="mt-24 md:mt-32 max-w-3xl" data-testid="reviews-section">
        <div className="flex flex-wrap items-center gap-4">
          <h2 className="font-serif text-2xl sm:text-3xl font-light tracking-tight">Reviews</h2>
          {reviewData.count > 0 && (
            <span className="flex items-center gap-2 text-sm text-taupe" data-testid="review-summary">
              <Stars value={reviewData.average} /> {reviewData.average} · {reviewData.count} review{reviewData.count > 1 ? "s" : ""}
            </span>
          )}
        </div>
        {reviewData.count === 0 && (
          <p className="text-sm text-taupe mt-4" data-testid="reviews-empty">No reviews yet — be the first to share your experience.</p>
        )}
        <div className="mt-8 space-y-6">
          {reviewData.reviews.slice(0, 6).map((r) => (
            <div key={r.id} className="border-b border-beige pb-5" data-testid={`review-${r.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <Stars value={r.rating} size="w-3.5 h-3.5" />
                <p className="text-sm font-medium">{r.name}</p>
                <p className="text-xs text-taupe">{new Date(r.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</p>
              </div>
              {r.text && <p className="text-sm text-espresso/75 leading-relaxed mt-2">{r.text}</p>}
            </div>
          ))}
        </div>
        <form onSubmit={submitReview} className="mt-10 bg-white border border-beige p-6 sm:p-8" data-testid="review-form">
          <p className="overline-label mb-4">Write a Review</p>
          <div className="flex items-center gap-1.5" data-testid="review-star-picker">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`review-star-${n}`} aria-label={`${n} star${n > 1 ? "s" : ""}`} onClick={() => setReviewForm({ ...reviewForm, rating: n })}>
                <Star className={`w-6 h-6 transition-colors ${n <= reviewForm.rating ? "fill-rose text-rose" : "text-beige hover:text-taupe"}`} strokeWidth={1.5} />
              </button>
            ))}
          </div>
          <input
            required
            className="input-lux mt-5"
            placeholder="Your name"
            data-testid="review-name-input"
            value={reviewForm.name}
            onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
          />
          <textarea
            rows={3}
            className="input-lux mt-3 resize-none"
            placeholder="What did you love about it? (optional)"
            data-testid="review-text-input"
            value={reviewForm.text}
            onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })}
          />
          <button type="submit" disabled={reviewBusy} className="btn-primary mt-5" data-testid="review-submit">
            {reviewBusy ? "Submitting…" : "Submit Review"}
          </button>
        </form>
      </section>

      {related.length > 0 && (
        <section className="mt-24 md:mt-32" data-testid="related-products">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl font-light tracking-tight" data-testid="related-title">{relatedTitle}</h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12 mt-10">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
